/**
 * Parent Home Page — Dashboard Layout
 * 
 * Status-first design: upcoming lessons at the top, actions below.
 * 1. Upcoming Lessons (primary section)
 * 2. Schedule a Lesson (compact link)
 * 3. Cards + Pages (compact side-by-side cards)
 */

import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  Calendar, 
  BookOpen, 
  HelpCircle, 
  Video, 
  Clock,
  ChevronRight,
  Plus,
} from 'lucide-react';
import { CardIcon } from '@/components/icons/CardIcon';
import { useAuth } from '@/lib/auth';
import { useViewAs } from '@/contexts/ViewAsContext';
import { useSelectedChild } from '@/contexts/ChildContext';
import { supabase } from '@/lib/supabase/client';
import { glassStyles } from '@/lib/design';
import { PageSpinner } from '@/components/ui/Spinner';
import { HelpModal, type HelpTopic } from '@/components/shared/HelpModal';
import { TrialStatusBar } from '@/components/shared/TrialStatusBar';
import { useParentStatus } from '@/hooks/useParentStatus';
import { useTranslation } from '@/lib/i18n';

// ============================================================================
// Types
// ============================================================================

interface UpcomingLesson {
  id: string;
  status: string;
  zoom_join_url: string | null;
  slot: {
    start_time: string;
    end_time: string;
  } | null;
}

// ============================================================================
// Main Component
// ============================================================================

export default function ParentHomePage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { profile } = useAuth();
  const { effectiveParentId } = useViewAs();
  const { selectedChild, children, selectedChildId, setSelectedChildId, isMultiChild, isLoading: childrenLoading } = useSelectedChild();

  // Data state
  const [lessonsLoading, setLessonsLoading] = useState(true);
  const [upcomingLessons, setUpcomingLessons] = useState<UpcomingLesson[]>([]);
  const [loadError, setLoadError] = useState(false);

  // UI state
  const [helpTopic, setHelpTopic] = useState<HelpTopic | null>(null);

  // Use effectiveParentId for data queries (supports admin preview via ?viewAs=)
  const parentId = effectiveParentId || profile?.id;

  // Parent status for trial info
  const parentStatus = useParentStatus({
    childId: selectedChildId ?? undefined,
    profileId: parentId
  });

  // Load upcoming lessons when selected child changes
  const loadLessons = async () => {
    if (!selectedChildId) {
      setUpcomingLessons([]);
      setLessonsLoading(false);
      return;
    }

    setLessonsLoading(true);
    setLoadError(false);

    try {
      const { data: lessonsData } = await supabase
        .from('scheduled_classes')
        .select(`
          id, 
          status, 
          zoom_join_url,
          slot:class_slots(start_time, end_time)
        `)
        .eq('child_id', selectedChildId)
        .in('status', ['scheduled', 'confirmed'])
        .gte('slot.start_time', new Date().toISOString())
        .order('slot(start_time)', { ascending: true })
        .limit(5);
      
      const futureLessons = (lessonsData || [])
        .filter(l => l.slot) as UpcomingLesson[];
      
      setUpcomingLessons(futureLessons);
    } catch (err) {
      console.error('Error loading lessons:', err);
      setLoadError(true);
    } finally {
      setLessonsLoading(false);
    }
  };

  useEffect(() => {
    loadLessons();
  }, [selectedChildId]);

  const isLoading = childrenLoading || lessonsLoading;
  
  // Format date for display
  const formatLessonDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    if (date.toDateString() === today.toDateString()) {
      return t('Today');
    } else if (date.toDateString() === tomorrow.toDateString()) {
      return t('Tomorrow');
    } else {
      return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
    }
  };
  
  const formatLessonTime = (dateStr: string) => {
    return new Date(dateStr).toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
  };
  
  // Loading state
  if (isLoading) {
    return <PageSpinner message="Loading..." />;
  }
  
  // Error state
  if (loadError) {
    return (
      <div className="min-h-screen bg-[#FAF6F0] flex items-center justify-center p-4">
        <div className="text-center py-12">
          <p className="text-[#636366] mb-4">{t('Something went wrong loading your data.')}</p>
          <button onClick={() => { setLoadError(false); loadLessons(); }} className="px-4 py-2 bg-[#5C2D91] text-white rounded-xl text-sm">
            {t('Try Again')}
          </button>
        </div>
      </div>
    );
  }

  // No children state
  if (children.length === 0) {
    return (
      <div className="min-h-screen bg-[#FAF6F0] flex items-center justify-center p-4">
        <div className="max-w-md w-full p-8 text-center rounded-2xl" style={glassStyles.card}>
          <h2 className="text-2xl font-semibold text-[#1C1C1E] mb-4">
            {t('Welcome to Turn')}
          </h2>
          <p className="text-[#636366] mb-6">
            {t('Add a child to your account to get started.')}
          </p>
          <Link
            to="/settings"
            className="inline-flex items-center gap-2 px-6 py-3 bg-[#5C2D91] hover:brightness-110 text-white rounded-xl font-medium transition-all"
          >
            {t('Add Your Child')}
          </Link>
        </div>
      </div>
    );
  }
  
  // Main Home View — Dashboard layout: status first, actions second
  return (
    <div className="min-h-screen bg-[#FAF6F0]">
      <div className="max-w-2xl mx-auto px-4 py-6">
        {/* Trial Status Card */}
        {parentStatus.isInTrial && (
          <TrialStatusBar status={parentStatus} />
        )}
        {/* Header */}
        <header className="mb-6">
          <h1 className="text-2xl font-semibold text-[#1C1C1E]">
            {t('Hi')}, {selectedChild?.name}!
          </h1>
          {isMultiChild && (
            <div className="flex items-center gap-2 mt-3">
              {children.map(child => (
                <button
                  key={child.id}
                  onClick={() => setSelectedChildId(child.id)}
                  className={`px-3 py-2.5 rounded-full text-sm font-medium transition-colors ${
                    selectedChildId === child.id
                      ? 'bg-[#5C2D91] text-white'
                      : 'bg-black/[0.03] text-[#636366] hover:bg-black/[0.05]'
                  }`}
                >
                  {child.name}
                </button>
              ))}
            </div>
          )}
        </header>
        
        {/* Upcoming Lessons — Primary dashboard section */}
        <section className="mb-5">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-medium text-[#1C1C1E] flex items-center gap-2">
              <Calendar className="w-5 h-5 text-[#5C2D91]" />
              {t('Upcoming Lessons')}
            </h2>
            <Link 
              to="/schedule"
              className="text-sm text-[#5C2D91] hover:brightness-110 flex items-center gap-1"
            >
              {t('View All')}
              <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
          
          {upcomingLessons.length > 0 ? (
            <div className="space-y-2.5">
              {upcomingLessons.slice(0, 2).map(lesson => (
                <div 
                  key={lesson.id}
                  className="p-3.5 rounded-2xl flex items-center justify-between"
                  style={glassStyles.card}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-[#5C2D91]/10 flex items-center justify-center">
                      <Video className="w-5 h-5 text-[#5C2D91]" />
                    </div>
                    <div>
                      <p className="font-medium text-[#1C1C1E] text-sm">
                        {lesson.slot && formatLessonDate(lesson.slot.start_time)}
                      </p>
                      <p className="text-xs text-[#8E8E93] flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {lesson.slot && formatLessonTime(lesson.slot.start_time)}
                      </p>
                    </div>
                  </div>
                  {lesson.zoom_join_url && (
                    <a
                      href={lesson.zoom_join_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-4 py-2.5 bg-[#34C759] hover:brightness-110 text-[#1C1C1E] text-xs font-medium rounded-xl transition-colors"
                    >
                      {t('Join')}
                    </a>
                  )}
                </div>
              ))}
              {upcomingLessons.length > 2 && (
                <Link 
                  to="/schedule"
                  className="block text-center text-sm text-[#5C2D91]/70 hover:text-[#5C2D91] py-2 transition-colors"
                >
                  +{upcomingLessons.length - 2} more lesson{upcomingLessons.length - 2 > 1 ? 's' : ''}  →
                </Link>
              )}
            </div>
          ) : (
            <div 
              className="p-5 rounded-2xl text-center"
              style={glassStyles.cardSubtle}
            >
              <p className="text-[#8E8E93] text-sm">
                {t('No upcoming lessons scheduled')}
              </p>
            </div>
          )}
        </section>
        
        {/* Schedule Lesson — Compact text link */}
        <button
          onClick={() => navigate('/schedule')}
          className="flex items-center gap-1.5 text-sm text-[#5C2D91] hover:brightness-110 transition-colors mb-6"
        >
          <Plus className="w-4 h-4" />
          {t('Schedule a Lesson')}
        </button>
        
        {/* Quick Actions — Compact side-by-side cards */}
        {(parentStatus.canAccessConsonant || parentStatus.canAccessLearn) && (
        <section className={`grid gap-3 ${parentStatus.canAccessConsonant && parentStatus.canAccessLearn ? 'grid-cols-2' : 'grid-cols-1'}`}>
          {parentStatus.canAccessConsonant && (
          <button
            onClick={() => navigate(selectedChild?.id ? `/consonant-practice?child=${selectedChild.id}` : '/consonant-practice')}
            className="relative p-4 rounded-2xl flex flex-col items-center gap-3 transition-all hover:brightness-105 focus:ring-2 focus:ring-white/20 focus:outline-none"
            style={glassStyles.card}
          >
            <div className="w-11 h-11 rounded-xl bg-[#34C759]/10 flex items-center justify-center">
              <CardIcon className="w-5 h-5 text-[#34C759]" />
            </div>
            <span className="font-medium text-[#1C1C1E] text-sm">{t('Cards')}</span>
            <button
              onClick={(e) => { e.stopPropagation(); setHelpTopic('consonant-practice'); }}
              className="absolute top-2 right-2 p-2.5 min-w-[44px] min-h-[44px] flex items-center justify-center rounded-lg hover:bg-black/[0.05] text-[#C7C7CC] hover:text-[#636366] transition-colors"
              aria-label="Learn more about Cards"
            >
              <HelpCircle className="w-4 h-4" />
            </button>
          </button>
          )}
          
          {parentStatus.canAccessLearn && (
          <button
            onClick={() => navigate(selectedChild?.id ? `/read?child=${selectedChild.id}` : '/read')}
            className="relative p-4 rounded-2xl flex flex-col items-center gap-3 transition-all hover:brightness-105 focus:ring-2 focus:ring-white/20 focus:outline-none"
            style={glassStyles.card}
          >
            <div className="w-11 h-11 rounded-xl bg-[#FF9500]/10 flex items-center justify-center">
              <BookOpen className="w-5 h-5 text-[#FF9500]" />
            </div>
            <span className="font-medium text-[#1C1C1E] text-sm">{t('Pages')}</span>
            <button
              onClick={(e) => { e.stopPropagation(); setHelpTopic('read-foundations'); }}
              className="absolute top-2 right-2 p-2.5 min-w-[44px] min-h-[44px] flex items-center justify-center rounded-lg hover:bg-black/[0.05] text-[#C7C7CC] hover:text-[#636366] transition-colors"
              aria-label="Learn more about Pages"
            >
              <HelpCircle className="w-4 h-4" />
            </button>
          </button>
          )}
        </section>
        )}
      </div>
      
      {/* Help Modal */}
      <HelpModal
        topic={helpTopic || 'schedule'}
        isOpen={helpTopic !== null}
        onClose={() => setHelpTopic(null)}
      />
    </div>
  );
}
